// frontend/src/components/TransactionType/TransactionType.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Receipt, Plus, Building, ArrowRightLeft, FileText,
  Save, X, Loader2, AlertCircle, ChevronDown, Hash, DollarSign,
  Package, ShoppingCart, Truck, RefreshCw, TrendingUp, TrendingDown, Layers,
  ClipboardList, Calendar, Clock, Upload
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./TransactionType.css";

const url = process.env.REACT_APP_BACKEND_URL;

const TransactionType = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [installingStandards, setInstallingStandards] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});

  // For system admins: company selection
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Predefined transaction types
  const predefinedTypes = [
    { transaction_type: "opening_balance", reference_type: "OPN", sign: "+", notes: "Initial stock entry", icon: Package, category: "stock" },
    { transaction_type: "purchase", reference_type: "GRN", sign: "+", notes: "Stock increases from purchase", icon: ShoppingCart, category: "stock" },
    { transaction_type: "sale", reference_type: "INV", sign: "-", notes: "Stock decreases from sale", icon: DollarSign, category: "stock" },
    { transaction_type: "sales_return", reference_type: "SRT", sign: "+", notes: "Customer returned stock → increases", icon: RefreshCw, category: "stock" },
    { transaction_type: "purchase_return", reference_type: "PRT", sign: "-", notes: "Returned to supplier → decreases stock", icon: Truck, category: "stock" },
    { transaction_type: "transfer_in", reference_type: "TRI", sign: "+", notes: "Received from another warehouse → increases stock", icon: ArrowRightLeft, category: "transfer" },
    { transaction_type: "transfer_out", reference_type: "TRO", sign: "-", notes: "Sent to another warehouse → decreases stock", icon: ArrowRightLeft, category: "transfer" },
    { transaction_type: "stock_adjustment_in", reference_type: "ADJ-IN", sign: "+", notes: "Manual stock adjustment increasing inventory (e.g., found extra stock, correction)", icon: TrendingUp, category: "adjustment" },
    { transaction_type: "stock_adjustment_out", reference_type: "ADJ-OUT", sign: "-", notes: "Manual stock adjustment decreasing inventory (e.g., damaged, lost, discrepancy)", icon: TrendingDown, category: "adjustment" },
    { transaction_type: "stock_count", reference_type: "SCC", sign: "0", notes: "Physical stock count only; differences post through adjustment-in or adjustment-out", icon: Layers, category: "adjustment" },
    { transaction_type: "sales_order", reference_type: "SO", sign: "0", notes: "Sales order - reservation only → affects reserved_qty, not stock quantity", icon: ClipboardList, category: "order" },
    { transaction_type: "purchase_order", reference_type: "PO", sign: "0", notes: "Purchase order - reservation only → affects reserved_qty, not stock quantity", icon: ClipboardList, category: "order" }
  ];

  const groupedTypes = {
    stock: predefinedTypes.filter(t => t.category === "stock"),
    transfer: predefinedTypes.filter(t => t.category === "transfer"),
    adjustment: predefinedTypes.filter(t => t.category === "adjustment"),
    order: predefinedTypes.filter(t => t.category === "order")
  };

  const [formData, setFormData] = useState({
    transaction_type: "",
    reference_type: "",
    sign: "+",
    notes: "",
    description: ""
  });

  const isSystemAdminUser = () =>
    userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  const isCompanyAdminUser = () =>
    userType === 'company' && currentUser?.role === 'admin';

  const userCanCreateTransactionType = () =>
    isCompanyAdminUser() || isSystemAdminUser();

  const handleRequestError = (error, fallbackMessage = "Request failed") => {
    const responseData = error.response?.data;

    if (responseData?.subscriptionExpired) {
      const title = responseData.title || "Subscription Required";
      const message =
        responseData.message || "Your company subscription is not active.";

      localStorage.setItem("subscriptionBlocked", JSON.stringify(responseData));

      toast.error(`${title}: ${message}`, { autoClose: 5000 });

      window.dispatchEvent(
        new CustomEvent("subscription-expired", { detail: responseData }),
      );

      window.setTimeout(() => {
        navigate("/MySubscription", { replace: true });
      }, 800);

      return true;
    }

    toast.error(responseData?.message || fallbackMessage);
    return false;
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      handleRequestError(error, "Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    const checkPermissionsAndSetup = async () => {
      try {
        if (!userCanCreateTransactionType()) {
          toast.error("You don't have permission to create transaction types");
          navigate("/");
          return;
        }

        if (isSystemAdminUser()) {
          await fetchCompanies();
        } else if (userType === 'company' && company) {
          const companyId = company._id || company;
          setFormData(prev => ({ ...prev, company_id: companyId }));
        }

        setLoading(false);
      } catch (error) {
        toast.error("Failed to load permissions");
        navigate("/");
      }
    };

    checkPermissionsAndSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;
    if (name === 'transaction_type') {
      processedValue = value.toLowerCase();
    } else if (name === 'reference_type') {
      processedValue = value.toUpperCase();
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleCompanyChange = (e) => {
    const value = e.target.value;
    setSelectedCompany(value);
    setFormData(prev => ({ ...prev, company_id: value }));
  };

  const handlePredefinedTypeSelect = (type) => {
    setFormData({
      transaction_type: type.transaction_type,
      reference_type: type.reference_type,
      sign: type.sign,
      notes: type.notes,
      description: type.notes,
      company_id: formData.company_id
    });

    toast.info(`Selected: ${type.transaction_type}`);
  };

  // ----------------------------------------------------------------
  // Install standard types — on the FORM page this should also return
  // the user to /ViewTransactionType after success, so they can see
  // the newly installed rows immediately.
  // ----------------------------------------------------------------
  const handleInstallStandardTypes = async () => {
    if (!userCanCreateTransactionType()) {
      toast.error("Only company administrators and system administrators can install standard transaction types");
      return;
    }

    if (isSystemAdminUser() && !selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    const companyName = isSystemAdminUser()
      ? companies.find((item) => item._id === selectedCompany)?.company_name
      : company?.company_name || "your company";

    const confirmed = window.confirm(
      `Install all standard inventory transaction types for ${companyName}?\n\n` +
      "This is safe to run again: missing types are created and existing standard types are repaired without duplicates."
    );

    if (!confirmed) return;

    try {
      setInstallingStandards(true);
      const response = await axios.post(
        `${url}/api/transactiontype/standard-setup`,
        isSystemAdminUser() ? { company_id: selectedCompany } : {}
      );

      if (response.data.success) {
        const summary = response.data.summary || {};
        const parts = [];
        if (summary.created) parts.push(`${summary.created} created`);
        if (summary.repaired) parts.push(`${summary.repaired} repaired`);
        if (summary.renamed) parts.push(`${summary.renamed} renamed`);
        if (summary.merged) parts.push(`${summary.merged} merged`);
        if (summary.unchanged) parts.push(`${summary.unchanged} already correct`);

        const summaryText = parts.length ? parts.join(" · ") : "no changes were necessary";

        const nothingChanged =
          !summary.created &&
          !summary.repaired &&
          !summary.renamed &&
          !summary.merged;

        if (nothingChanged) {
          toast.info(
            `Standard transaction types were already installed for ${companyName}. ${summaryText}.`,
          );
        } else {
          toast.success(
            `Standard transaction types ready for ${companyName} — ${summaryText}.`,
          );
        }

        // -----------------------------------------------------------
        // Navigate back to the list — this is the same destination
        // the Create button uses, so both flows land in the same place.
        // The refreshAt state tells the list page to re-fetch.
        // -----------------------------------------------------------
        navigate("/ViewTransactionType", {
          replace: true,
          state: { refreshAt: Date.now() },
        });
      } else {
        toast.error(
          response.data.message || "Failed to install standard transaction types",
        );
      }
    } catch (error) {
      handleRequestError(
        error,
        "Failed to install standard transaction types"
      );
    } finally {
      setInstallingStandards(false);
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) =>
    setFocused({ ...focused, [field]: formData[field] && formData[field].length > 0 });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.transaction_type.trim()) {
      newErrors.transaction_type = "Transaction type is required";
    } else if (formData.transaction_type.length > 50) {
      newErrors.transaction_type = "Transaction type cannot exceed 50 characters";
    }

    if (!formData.reference_type.trim()) {
      newErrors.reference_type = "Reference type is required";
    } else if (formData.reference_type.length > 10) {
      newErrors.reference_type = "Reference type cannot exceed 10 characters";
    }

    if (!formData.sign) {
      newErrors.sign = "Sign is required";
    }

    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = "Notes cannot exceed 500 characters";
    }

    if (!formData.company_id) {
      newErrors.company_id = "Company selection is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
        description: formData.notes,
      };

      const response = await axios.post(`${url}/api/transactiontype`, submitData);

      if (response.data.success) {
        toast.success(
          response.data.message || "Transaction type created successfully!",
        );

        setFormData({
          transaction_type: "",
          reference_type: "",
          sign: "+",
          notes: "",
          description: "",
          company_id: userType === "company" ? company?._id || company : "",
        });
        setSelectedCompany("");

        navigate("/ViewTransactionType", {
          replace: true,
          state: { refreshAt: Date.now() },
        });
      }
    } catch (error) {
      const subscriptionHandled = handleRequestError(
        error,
        "Failed to create transaction type",
      );

      if (!subscriptionHandled && error.response?.data?.errors) {
        setErrors((previous) => ({
          ...previous,
          ...Object.fromEntries(
            error.response.data.errors.map((item) => [
              item.field,
              item.message,
            ]),
          ),
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewTransactionType");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="transactiontype-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="transactiontype-form-container">
        <button className="close-btn" onClick={handleCancel} title="Close">
          <X size={20} />
        </button>

        <div className="form-header">
          <Receipt size={24} />
          <h2 className="form-title">Add New Transaction Type</h2>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <p className="form-subtitle" style={{ margin: 0 }}>
              {isSystemAdminUser()
                ? "Create a transaction type for any company"
                : `Create a transaction type for ${company?.company_name || "your company"}`}
            </p>

            {userCanCreateTransactionType() && (
              <button
                type="button"
                className="system-defaults-btn"
                onClick={handleInstallStandardTypes}
                disabled={
                  installingStandards ||
                  (isSystemAdminUser() && !selectedCompany)
                }
                title={
                  isSystemAdminUser() && !selectedCompany
                    ? "Select a company first"
                    : "Create or repair all standard transaction types in one action"
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 16px",
                  background: "#667eea",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor:
                    installingStandards ||
                    (isSystemAdminUser() && !selectedCompany)
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    installingStandards ||
                    (isSystemAdminUser() && !selectedCompany)
                      ? 0.65
                      : 1,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {installingStandards ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Installing…
                  </>
                ) : (
                  <>
                    <Upload size={16} /> Install standard types
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div
          className="role-indicator"
          style={{
            background: isSystemAdminUser()
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #f89a3a 0%, #f55a42 100%)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Building size={20} />
          <div>
            <strong>
              {isSystemAdminUser() ? "System Administrator" : "Company Admin"}
            </strong>
            <div style={{ fontSize: "12px", opacity: 0.9 }}>
              {isSystemAdminUser()
                ? "You can create transaction types for any company"
                : `You can only create transaction types for ${company?.company_name || "your company"}`}
            </div>
          </div>
        </div>

        {/* Predefined Types Quick Select */}
        <div className="quick-select-container">
          <h4 className="quick-select-title">
            <Package size={16} className="mr-2" />
            Quick Select Standard Types
          </h4>
          <p className="quick-select-subtitle">
            Click on a standard type to auto-fill the form
          </p>

          <div className="category-section">
            <h5 className="category-title">
              <Package size={14} /> Stock Management
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.stock.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`stock-${index}`}
                    type="button"
                    className="predefined-type-card"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.notes}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.transaction_type}</div>
                    </div>
                    <div className="type-card-details">
                      <div className="type-ref">
                        Ref: <strong>{type.reference_type}</strong>
                      </div>
                      <div
                        className={`type-sign ${
                          type.sign === "+"
                            ? "positive"
                            : type.sign === "-"
                              ? "negative"
                              : "neutral"
                        }`}
                      >
                        Sign: <strong>{type.sign}</strong>
                      </div>
                    </div>
                    <div className="type-notes">{type.notes}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="category-section">
            <h5 className="category-title">
              <TrendingUp size={14} /> Stock Adjustments
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.adjustment.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`adj-${index}`}
                    type="button"
                    className="predefined-type-card adjustment"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.notes}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.transaction_type}</div>
                    </div>
                    <div className="type-card-details">
                      <div className="type-ref">
                        Ref: <strong>{type.reference_type}</strong>
                      </div>
                      <div
                        className={`type-sign ${
                          type.sign === "+"
                            ? "positive"
                            : type.sign === "-"
                              ? "negative"
                              : type.sign === "0"
                                ? "neutral"
                                : ""
                        }`}
                      >
                        Sign: <strong>{type.sign}</strong>
                      </div>
                    </div>
                    <div className="type-notes">{type.notes}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="category-section">
            <h5 className="category-title">
              <ArrowRightLeft size={14} /> Transfers
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.transfer.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`transfer-${index}`}
                    type="button"
                    className="predefined-type-card"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.notes}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.transaction_type}</div>
                    </div>
                    <div className="type-card-details">
                      <div className="type-ref">
                        Ref: <strong>{type.reference_type}</strong>
                      </div>
                      <div
                        className={`type-sign ${
                          type.sign === "+"
                            ? "positive"
                            : type.sign === "-"
                              ? "negative"
                              : "neutral"
                        }`}
                      >
                        Sign: <strong>{type.sign}</strong>
                      </div>
                    </div>
                    <div className="type-notes">{type.notes}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="category-section">
            <h5 className="category-title">
              <ClipboardList size={14} /> Orders
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.order.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`order-${index}`}
                    type="button"
                    className="predefined-type-card order"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.notes}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.transaction_type}</div>
                    </div>
                    <div className="type-card-details">
                      <div className="type-ref">
                        Ref: <strong>{type.reference_type}</strong>
                      </div>
                      <div
                        className={`type-sign ${
                          type.sign === "+"
                            ? "positive"
                            : type.sign === "-"
                              ? "negative"
                              : "neutral"
                        }`}
                      >
                        Sign: <strong>{type.sign}</strong>
                      </div>
                    </div>
                    <div className="type-notes">{type.notes}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <form className="transactiontype-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {isSystemAdminUser() && (
              <div
                className={`form-field required ${
                  focused.company_id || formData.company_id ? "filled" : ""
                } ${errors.company_id ? "error" : ""}`}
              >
                <div className="select-wrapper">
                  <select
                    name="company_id"
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("company_id")}
                    onBlur={() => handleBlur("company_id")}
                    required
                    disabled={loadingCompanies}
                  >
                    <option value="">Select Company</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
                <label>Company *</label>
                {errors.company_id && (
                  <small className="field-error">{errors.company_id}</small>
                )}
                {loadingCompanies && (
                  <small className="field-hint">Loading companies...</small>
                )}
              </div>
            )}

            <div
              className={`form-field required ${
                focused.transaction_type || formData.transaction_type ? "filled" : ""
              } ${errors.transaction_type ? "error" : ""}`}
            >
              <input
                type="text"
                name="transaction_type"
                value={formData.transaction_type}
                onChange={handleChange}
                onFocus={() => handleFocus("transaction_type")}
                onBlur={() => handleBlur("transaction_type")}
                placeholder="e.g., stock_adjustment_in, opening_balance, purchase, sales_order, purchase_order"
                required
                maxLength={50}
                style={{ textTransform: "lowercase" }}
              />
              <label>Transaction Type *</label>
            </div>

            <div
              className={`form-field required ${
                focused.reference_type || formData.reference_type ? "filled" : ""
              } ${errors.reference_type ? "error" : ""}`}
            >
              <input
                type="text"
                name="reference_type"
                value={formData.reference_type}
                onChange={handleChange}
                onFocus={() => handleFocus("reference_type")}
                onBlur={() => handleBlur("reference_type")}
                placeholder="e.g., ADJ, OPN, GRN, INV, RTN, SO, PO, TRF"
                required
                maxLength={10}
                style={{ textTransform: "uppercase" }}
              />
              <label>Reference Type *</label>
            </div>

            <div className={`form-field required ${errors.sign ? "error" : ""}`}>
              <div className="select-wrapper">
                <select
                  name="sign"
                  value={formData.sign}
                  onChange={handleChange}
                  required
                >
                  <option value="+">+ (Positive/Increase Stock)</option>
                  <option value="-">- (Negative/Decrease Stock)</option>
                  <option value="0">0 (Neutral/No Stock Effect)</option>
                </select>
                <ArrowRightLeft size={16} className="select-icon" />
              </div>
              <label>Sign *</label>
              {errors.sign && <small className="field-error">{errors.sign}</small>}
              <small className="field-hint">
                Effect on stock quantity (+ increases, - decreases, 0 no effect)
              </small>
            </div>

            <div
              className={`form-field ${
                focused.notes || formData.notes ? "filled" : ""
              } ${errors.notes ? "error" : ""}`}
            >
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                onFocus={() => handleFocus("notes")}
                onBlur={() => handleBlur("notes")}
                placeholder="Describe the purpose and effect of this transaction type"
                rows="3"
                maxLength={500}
              />
              <label>Description / Notes</label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  Create Transaction Type
                  <Save className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionType;